import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

type ChipPopoverProps = {
  label?: string;
  value: ReactNode;
  suffix?: ReactNode;
  icon?: ReactNode;
  popWidth?: number;
  popAlign?: 'left' | 'right';
  marginLeftAuto?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: (close: () => void) => ReactNode;
};

export const ChipPopover: FC<ChipPopoverProps> = ({
  label,
  value,
  suffix,
  icon,
  popWidth = 380,
  popAlign = 'left',
  marginLeftAuto,
  isOpen: controlledOpen,
  onOpenChange,
  children,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = (next: boolean) => {
    if (onOpenChange) onOpenChange(next);
    setInternalOpen(next);
  };
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current) return;
      if (e.target instanceof Node && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <span
      ref={ref}
      tabIndex={0}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('[data-cp-pop]')) return;
        setOpen(!open);
      }}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '7px 11px',
        border: open ? '1px solid #1452E6' : '1px solid #DEE2E8',
        background: '#FFFFFF',
        borderRadius: 10,
        fontSize: 12.5,
        color: '#344155',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        boxShadow: open ? '0 0 0 3px #E8EFFF' : 'none',
        marginLeft: marginLeftAuto ? 'auto' : undefined,
        outline: 'none',
      }}
    >
      {icon}
      {label ? <span style={{ color: '#7A8597' }}>{label}</span> : null}
      <span style={{ color: '#0B1733', fontWeight: 600 }}>{value}</span>
      {suffix}
      <ChevronDown size={12} color='currentColor' />
      {open ? (
        <div
          data-cp-pop
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: popAlign === 'left' ? 0 : 'auto',
            right: popAlign === 'right' ? 0 : 'auto',
            background: '#FFFFFF',
            border: '1px solid #DEE2E8',
            borderRadius: 14,
            boxShadow: '0 18px 50px rgba(11,23,51,0.18), 0 4px 12px rgba(11,23,51,0.08)',
            zIndex: 100,
            fontSize: 13,
            cursor: 'default',
            textAlign: 'left',
            width: popWidth,
            color: '#344155',
            whiteSpace: 'normal',
          }}
        >
          {children(close)}
        </div>
      ) : null}
    </span>
  );
};
